export const SHORTLIST_STORAGE_KEY = 'hapisumu:reform-case-shortlist:v1'
export const SHORTLIST_SCHEMA_VERSION = 1
export const SHORTLIST_TTL_MS = 180 * 24 * 60 * 60 * 1000

const MAX_PATH_LENGTH = 300
const MAX_IMAGE_PATH_LENGTH = 500
const MAX_TITLE_LENGTH = 120
const MAX_COMPANY_NAME_LENGTH = 120
const MAX_PERIOD_LENGTH = 40
const MAX_PARTS = 8
const MAX_PART_LENGTH = 40
const MAX_COST = 1_000_000_000
const TRUSTED_IMAGE_ORIGINS = new Set([
  'https://new-img.hapisumu.jp',
])

function isPlainObject(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function characterLength(value) {
  return Array.from(value).length
}

function normalizeText(value, maxLength, { required = false } = {}) {
  if (typeof value !== 'string') return null

  const normalized = value.trim().replace(/\s+/g, ' ')
  if (required && normalized === '') return null
  if (characterLength(normalized) > maxLength) return null

  return normalized
}

function normalizePath(value, origin, maxLength) {
  if (typeof value !== 'string' || value === '' || characterLength(value) > maxLength) return null
  if (!value.startsWith('/') || value.startsWith('//') || /[\u0000-\u001f\u007f\\]/.test(value)) return null

  try {
    const url = new URL(value, origin)
    if (url.origin !== origin || url.search !== '' || url.hash !== '' || url.pathname !== value) return null
  } catch (_error) {
    return null
  }

  return value
}

export function normalizeShortlistImagePath(value, origin = window.location.origin) {
  if (typeof value !== 'string' || value === '' || characterLength(value) > MAX_IMAGE_PATH_LENGTH) return null

  const localPath = normalizePath(value, origin, MAX_IMAGE_PATH_LENGTH)
  if (localPath !== null) return localPath
  if (/[\u0000-\u001f\u007f\\]/.test(value)) return null

  try {
    const url = new URL(value, origin)
    if (
      url.username !== ''
      || url.password !== ''
      || url.search !== ''
      || url.hash !== ''
    ) return null
    if (url.origin === origin) return normalizePath(url.pathname, origin, MAX_IMAGE_PATH_LENGTH)
    if (!TRUSTED_IMAGE_ORIGINS.has(url.origin)) return null
    return characterLength(url.href) <= MAX_IMAGE_PATH_LENGTH ? url.href : null
  } catch (_error) {
    return null
  }
}

function normalizeSnapshot(value, origin) {
  if (!isPlainObject(value)) return null

  const title = normalizeText(value.title, MAX_TITLE_LENGTH, { required: true })
  const companyName = normalizeText(value.companyName, MAX_COMPANY_NAME_LENGTH, { required: true })
  const period = normalizeText(value.period, MAX_PERIOD_LENGTH)
  if (title === null || companyName === null || period === null) return null

  let imagePath = null
  if (value.imagePath !== null) {
    imagePath = normalizeShortlistImagePath(value.imagePath, origin)
    if (imagePath === null) return null
  }

  let beforeImagePath = null
  if (value.beforeImagePath !== undefined && value.beforeImagePath !== null) {
    beforeImagePath = normalizeShortlistImagePath(value.beforeImagePath, origin)
    if (beforeImagePath === null) return null
  }

  let cost = null
  if (value.cost !== null) {
    if (!Number.isSafeInteger(value.cost) || value.cost <= 0 || value.cost > MAX_COST) return null
    cost = value.cost
  }

  if (!Array.isArray(value.parts) || value.parts.length > MAX_PARTS) return null
  const parts = []
  for (const rawPart of value.parts) {
    const part = normalizeText(rawPart, MAX_PART_LENGTH, { required: true })
    if (part === null) return null
    if (!parts.includes(part)) parts.push(part)
  }

  return { title, companyName, beforeImagePath, imagePath, cost, period, parts }
}

export function normalizeShortlistCandidate(value, origin = window.location.origin) {
  if (!isPlainObject(value)) return null
  if (!Number.isSafeInteger(value.caseId) || value.caseId <= 0) return null
  if (!Number.isSafeInteger(value.companyId) || value.companyId <= 0) return null

  const path = normalizePath(value.path, origin, MAX_PATH_LENGTH)
  const snapshot = normalizeSnapshot(value.snapshot, origin)
  if (path === null || snapshot === null) return null

  return { caseId: value.caseId, companyId: value.companyId, path, snapshot }
}

function normalizeStoredEntry(value, origin) {
  if (!isPlainObject(value)) return null
  if (!Number.isSafeInteger(value.savedAt) || value.savedAt <= 0) return null
  if (!Number.isSafeInteger(value.expiresAt) || value.expiresAt <= value.savedAt) return null
  if (value.expiresAt - value.savedAt !== SHORTLIST_TTL_MS) return null

  const candidate = normalizeShortlistCandidate(value, origin)
  return candidate === null ? null : { ...candidate, savedAt: value.savedAt, expiresAt: value.expiresAt }
}

function emptyState() {
  return { schemaVersion: SHORTLIST_SCHEMA_VERSION, cases: [] }
}

function parseStoredState(rawValue, now, origin) {
  if (rawValue === null) return { status: 'ok', state: emptyState(), shouldPersist: false }

  let value
  try {
    value = JSON.parse(rawValue)
  } catch (_error) {
    return { status: 'invalid' }
  }

  if (!isPlainObject(value)) return { status: 'invalid' }
  if (value.schemaVersion !== SHORTLIST_SCHEMA_VERSION) return { status: 'unsupported' }
  if (!Array.isArray(value.cases)) return { status: 'invalid' }

  const cases = []
  const caseIds = new Set()
  for (const rawEntry of value.cases) {
    const entry = normalizeStoredEntry(rawEntry, origin)
    if (entry === null || caseIds.has(entry.caseId)) return { status: 'invalid' }
    caseIds.add(entry.caseId)
    if (entry.expiresAt > now) cases.push(entry)
  }

  return {
    status: 'ok',
    state: { schemaVersion: SHORTLIST_SCHEMA_VERSION, cases },
    shouldPersist: cases.length !== value.cases.length,
  }
}

function resolveStorage(options) {
  if (Object.prototype.hasOwnProperty.call(options, 'storage')) return options.storage
  try {
    return window.localStorage
  } catch (_error) {
    return null
  }
}

function isStorageLike(storage) {
  return storage !== null
    && typeof storage.getItem === 'function'
    && typeof storage.setItem === 'function'
    && typeof storage.removeItem === 'function'
}

export function createShortlistStore(options = {}) {
  const storage = resolveStorage(options)
  const nowProvider = typeof options.now === 'function' ? options.now : () => Date.now()
  const origin = typeof options.origin === 'string' ? options.origin : window.location.origin

  function now() {
    const value = nowProvider()
    return Number.isSafeInteger(value) && value > 0 ? value : Date.now()
  }

  function persist(state) {
    try {
      storage.setItem(SHORTLIST_STORAGE_KEY, JSON.stringify(state))
      return true
    } catch (_error) {
      return false
    }
  }

  function readInternal() {
    if (!isStorageLike(storage)) return { status: 'unavailable' }

    let rawValue
    try {
      rawValue = storage.getItem(SHORTLIST_STORAGE_KEY)
    } catch (_error) {
      return { status: 'unavailable' }
    }

    const parsed = parseStoredState(rawValue, now(), origin)
    if (parsed.status === 'unsupported') return parsed
    if (parsed.status === 'invalid') {
      try {
        storage.removeItem(SHORTLIST_STORAGE_KEY)
      } catch (_error) {
        return { status: 'unavailable' }
      }
      return { status: 'ok', state: emptyState(), recovered: true }
    }
    if (parsed.shouldPersist) persist(parsed.state)
    return parsed
  }

  function actionFailure(result) {
    if (result.status === 'ok') return null
    return { ok: false, reason: result.status === 'unsupported' ? 'unsupported-schema' : 'storage-unavailable' }
  }

  function read() {
    const result = readInternal()
    const failure = actionFailure(result)
    if (failure !== null) return { ...failure, cases: [], count: 0 }
    return {
      ok: true,
      cases: result.state.cases,
      count: result.state.cases.length,
      recovered: result.recovered === true,
    }
  }

  function save(candidate) {
    const normalizedCandidate = normalizeShortlistCandidate(candidate, origin)
    if (normalizedCandidate === null) return { ok: false, reason: 'invalid-candidate' }

    const result = readInternal()
    const failure = actionFailure(result)
    if (failure !== null) return failure

    const isSaved = result.state.cases.some((entry) => entry.caseId === normalizedCandidate.caseId)
    if (isSaved) return { ok: true, saved: true, count: result.state.cases.length }
    const savedAt = now()
    const entry = { ...normalizedCandidate, savedAt, expiresAt: savedAt + SHORTLIST_TTL_MS }
    const cases = [...result.state.cases, entry]
    if (!persist({ ...result.state, cases })) return { ok: false, reason: 'storage-unavailable' }
    return { ok: true, saved: true, count: cases.length }
  }

  return { read, save }
}
